import { useState } from "react";

export type AssessmentResponses = {
  // Physical
  weekly_active_days: number;       // 0-7
  sleep_duration_hours: number;     // 4-10
  self_energy: number;              // 1-10

  // Emotional
  baseline_mood: number;            // 1-10
  baseline_stress: number;          // 1-10
  in_therapy: "yes" | "considering" | "no";

  // Mental
  meditation_frequency: "daily" | "weekly" | "occasional" | "never";
  focus_clarity: number;            // 1-10

  // Spiritual
  has_coach: "yes" | "interested" | "no";
  sense_of_purpose: number;         // 1-10

  // Social
  community_per_month: number;      // 0-10+
  feels_connected: number;          // 1-10
};

type Props = {
  firstName: string;
  onComplete: (r: AssessmentResponses) => void;
  onBack: () => void;
};

type Question =
  | { type: "scale"; key: keyof AssessmentResponses; min: number; max: number; minLabel: string; maxLabel: string; prompt: string; help?: string }
  | { type: "choice"; key: keyof AssessmentResponses; prompt: string; help?: string; options: Array<{ value: string; label: string; desc?: string }> }
  | { type: "number"; key: keyof AssessmentResponses; min: number; max: number; prompt: string; suffix?: string; help?: string };

const QUESTIONS: Question[] = [
  // Physical
  {
    type: "number",
    key: "weekly_active_days",
    prompt: "How many days a week are you typically physically active?",
    help: "Yoga, walking, lifting, sports — anything that gets your heart rate up.",
    min: 0, max: 7, suffix: "days",
  },
  {
    type: "scale",
    key: "sleep_duration_hours",
    prompt: "On average, how many hours do you sleep per night?",
    min: 4, max: 10, minLabel: "4 hrs", maxLabel: "10 hrs",
  },
  {
    type: "scale",
    key: "self_energy",
    prompt: "How would you rate your energy levels lately?",
    min: 1, max: 10, minLabel: "Drained", maxLabel: "Vibrant",
  },

  // Emotional
  {
    type: "scale",
    key: "baseline_mood",
    prompt: "Over the past two weeks, how has your mood been overall?",
    min: 1, max: 10, minLabel: "Low", maxLabel: "Buoyant",
  },
  {
    type: "scale",
    key: "baseline_stress",
    prompt: "And your stress level?",
    min: 1, max: 10, minLabel: "Calm", maxLabel: "Overwhelmed",
  },
  {
    type: "choice",
    key: "in_therapy",
    prompt: "Are you currently working with a counselor or therapist?",
    options: [
      { value: "yes",         label: "Yes, currently" },
      { value: "considering", label: "Considering it" },
      { value: "no",          label: "Not at this time" },
    ],
  },

  // Mental
  {
    type: "choice",
    key: "meditation_frequency",
    prompt: "How often do you meditate or do mindfulness practice?",
    options: [
      { value: "daily",      label: "Daily" },
      { value: "weekly",     label: "A few times a week" },
      { value: "occasional", label: "Once in a while" },
      { value: "never",      label: "I haven't yet" },
    ],
  },
  {
    type: "scale",
    key: "focus_clarity",
    prompt: "How would you describe your mental focus and clarity?",
    min: 1, max: 10, minLabel: "Foggy", maxLabel: "Sharp",
  },

  // Spiritual
  {
    type: "choice",
    key: "has_coach",
    prompt: "Are you working with a life or executive coach?",
    options: [
      { value: "yes",        label: "Yes" },
      { value: "interested", label: "I'm open to it" },
      { value: "no",         label: "Not right now" },
    ],
  },
  {
    type: "scale",
    key: "sense_of_purpose",
    prompt: "How connected do you feel to a sense of purpose or meaning?",
    min: 1, max: 10, minLabel: "Searching", maxLabel: "Aligned",
  },

  // Social
  {
    type: "number",
    key: "community_per_month",
    prompt: "How many community events or group activities do you typically join per month?",
    min: 0, max: 10, suffix: "events",
  },
  {
    type: "scale",
    key: "feels_connected",
    prompt: "How connected do you feel to people in your life?",
    min: 1, max: 10, minLabel: "Isolated", maxLabel: "Surrounded",
  },
];

export function WellnessAssessment({ firstName, onComplete, onBack }: Props) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Partial<AssessmentResponses>>({});

  const q = QUESTIONS[idx];
  const isLast = idx === QUESTIONS.length - 1;
  const isFirst = idx === 0;
  const value = answers[q.key];

  const setAnswer = (val: any) => setAnswers({ ...answers, [q.key]: val });

  const next = () => {
    if (isLast) {
      onComplete(answers as AssessmentResponses);
    } else {
      setIdx(idx + 1);
    }
  };

  const back = () => {
    if (isFirst) onBack();
    else setIdx(idx - 1);
  };

  const canAdvance = value !== undefined && value !== null /* tsc: no empty-string case */;

  return (
    <div>
      <div className="text-center mb-8">
        <div className="text-[10px] tracking-[0.32em] text-teal font-medium uppercase mb-3">
          Assessment · {idx + 1} of {QUESTIONS.length}
        </div>
        <div className="h-0.5 bg-sand rounded-full overflow-hidden max-w-xs mx-auto">
          <div
            className="h-full bg-teal transition-all duration-500"
            style={{ width: `${((idx + 1) / QUESTIONS.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-cardBorder p-8 md:p-10 min-h-[280px]">
        <h2 className="font-serif text-2xl md:text-3xl text-textPrimary font-light leading-tight mb-2">
          {q.prompt}
        </h2>
        {q.help && <p className="text-textBody text-sm leading-relaxed mb-6">{q.help}</p>}
        <div className={q.help ? "" : "mt-6"}>

          {q.type === "scale" && (
            <div>
              <div className="flex justify-between mb-1.5 text-xs tracking-wider uppercase text-textBody">
                <span>{q.minLabel}</span>
                <span className="font-serif text-2xl text-teal">{(value as number | undefined) ?? "—"}</span>
                <span>{q.maxLabel}</span>
              </div>
              <input
                type="range"
                min={q.min}
                max={q.max}
                step={q.key === "sleep_duration_hours" ? 0.5 : 1}
                value={(value as number | undefined) ?? Math.floor((q.min + q.max) / 2)}
                onChange={(e) => setAnswer(Number(e.target.value))}
                onMouseDown={() => { if (value === undefined) setAnswer(Math.floor((q.min + q.max) / 2)); }}
                onTouchStart={() => { if (value === undefined) setAnswer(Math.floor((q.min + q.max) / 2)); }}
                className="w-full accent-teal"
              />
              {value === undefined && <p className="text-xs text-textBody opacity-60 mt-2 text-center">Drag to choose</p>}
            </div>
          )}

          {q.type === "number" && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setAnswer(Math.max(q.min, ((value as number | undefined) ?? q.min) - 1))}
                className="w-12 h-12 rounded-full bg-sand hover:bg-sandDark text-textPrimary font-serif text-2xl"
              >−</button>
              <div className="flex-1 text-center">
                <div className="font-serif text-5xl text-teal">{(value as number | undefined) ?? q.min}</div>
                {q.suffix && <div className="text-xs tracking-wider uppercase text-textBody mt-1">{q.suffix}</div>}
              </div>
              <button
                onClick={() => setAnswer(Math.min(q.max, ((value as number | undefined) ?? q.min) + 1))}
                className="w-12 h-12 rounded-full bg-sand hover:bg-sandDark text-textPrimary font-serif text-2xl"
              >+</button>
            </div>
          )}

          {q.type === "choice" && (
            <div className="space-y-2.5">
              {q.options.map((opt) => {
                const selected = value === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setAnswer(opt.value)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition ${
                      selected ? "border-teal bg-teal/5" : "border-cardBorder hover:border-teal/40 bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-textPrimary font-medium text-sm">{opt.label}</span>
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        selected ? "border-teal bg-teal" : "border-cardBorder"
                      }`} />
                    </div>
                    {opt.desc && <p className="text-xs text-textBody mt-1.5">{opt.desc}</p>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-between items-center">
        <button onClick={back} className="text-textBody hover:text-teal text-xs tracking-wider uppercase">
          ← Back
        </button>
        <button
          onClick={next}
          disabled={!canAdvance}
          className="bg-teal hover:bg-tealDeep disabled:opacity-40 disabled:cursor-not-allowed text-white tracking-wider uppercase text-xs font-medium px-10 py-3.5 rounded-lg transition"
        >
          {isLast ? "Finish Assessment" : "Continue"}
        </button>
      </div>

      {idx === 0 && <p className="text-center text-xs text-textBody mt-6 italic opacity-70">There are no wrong answers, {firstName}. Be honest with yourself.</p>}
    </div>
  );
}
